/**
 * Nacteni a sprava konfigurace, konfigurace je nacitana ze serveru na zaklade API_KEY.
 * V konfiguraci jsou ulozeny staticke texty v jazyce chatbota a styly aplikace.
 * Automaticky prepina styly na tmavy/svetly rezim podle nastaveni prohlizece.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAxios } from './Axios';
import { ConfigurationFeatures, Configuration as ConfigurationModel, StylesConfiguration } from '../api/models';
import { useSessionId } from './SessionId';

export interface IConfiguration {
    mediaPath: ConfigurationModel['mediaPath']
    design?: ConfigurationModel['design']
    texts: ConfigurationModel['texts']
    styles: StylesConfiguration
    features: ConfigurationFeatures
}

type ConfigurationType = IConfiguration | undefined;

export const ConfigurationContext = createContext<ConfigurationType>(undefined);

export const useConfiguration = () => {
    return useContext(ConfigurationContext);
};

interface IConfigurationProps {
    children: any
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mock: ConfigurationModel = {
    "design": 'stx-chatbot-design-ns',
    "mediaPath": "http://krizak.styraxconsulting.cz/api/static/",
    "texts": {
        "name": "Efektivní nemocnice",
        "btnSendTitle": "Odeslat",
        "btnRenewTitle": "Zahájit novou konverzaci",
        "inputPlaceholder": "Napište Váš požadavek...",
        "messageRepeat": "opakovat dotaz",
        "btnExpandTitle": "Chci se zeptat chatbota",
        "btnCloseTitle": "Schovat chatbota",
        "messageError": "Jsem příliš zaneprázdněn, zkuste se zeptat později. :-(",
        "alert": "Dobrý den, tady chatbot Efektivní nemocnice. Mohu Vám pomoci?",
        "messageExpander": "více...",
        "messageCollapser": "méně..."
    },
    "styles": {
        "light": {
            "iconBgColor": "#1E60AA",
            "windowBgColor": "#FFF",
            "nameColor": "#FFF",
            "btnSendColor": "#0D1D2E",
            "btnRenewColor": "#0D1D2E",
            "inputBgColor": "#E8E8E8",
            "inputTextColor": "#000",
            "messageInBgColor": "#0D1D2E",
            "messageInTextColor": "#FFF",
            "messageOutBgColor": "#1E60AA",
            "messageOutTextColor": "#FFF",
            "messageErrorColor": "#ff5768",
            "messageInfoColor": "#74737a",
            "faqBgColor": "#1E60AA",
            "faqTextColor": "#FFF",
            "iconImage": "17da7565-ec94-4c52-90b8-5044aad70ba3.png",
            "fontFamily": "sans-serif",
            "nameBgColor": "#1E60AA",
            "btnSendBgColor": "#FFF",
            "btnRenewBgColor": "#FFF",
            "footerBgColor": "#FFF"
        },
        "dark": {
            "iconBgColor": "#1E60AA",
            "windowBgColor": "#FFF",
            "nameColor": "#FFF",
            "btnSendColor": "#0D1D2E",
            "btnRenewColor": "#0D1D2E",
            "inputBgColor": "#E8E8E8",
            "inputTextColor": "#000",
            "messageInBgColor": "#0D1D2E",
            "messageInTextColor": "#FFF",
            "messageOutBgColor": "#1E60AA",
            "messageOutTextColor": "#FFF",
            "messageErrorColor": "#ff5768",
            "messageInfoColor": "#74737a",
            "faqBgColor": "#1E60AA",
            "faqTextColor": "#FFF",
            "iconImage": "17da7565-ec94-4c52-90b8-5044aad70ba3.png",
            "fontFamily": "sans-serif",
            "nameBgColor": "#1E60AA",
            "btnSendBgColor": "#FFF",
            "btnRenewBgColor": "#FFF",
            "footerBgColor": "#FFF"
        }
    },
    "features": {
        "defaultOpen": false,
        "memoOpen": true,
        "onChatLinkClickAction": "AutoClose",
        "internalLinksTarget": "self",
        "externalLinksTarget": "new",
        "closeMode": "expander",
        "footerMode": "RIS",
        "alertTimer": 5,
        "incommingMessageShowRows": 4
    } as any
};

function Configuration({ children }: IConfigurationProps) {
    const [data, setData] = useState<ConfigurationModel | undefined>(undefined);
    const [dark, setDark] = useState<boolean>(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [config, setConfig] = useState<ConfigurationType>(undefined);
    const axios = useAxios();
    const sessionId = useSessionId();
    useEffect(
        () => {
            //hlidani zmen darkmode
            window
                .matchMedia('(prefers-color-scheme: dark)')
                .addEventListener('change', event => {
                    setDark(event.matches);
                });

            //nacteni konfigurace ze serveru
            axios.get<ConfigurationModel>('configuration')
                .then((response) => {
                    setData(response.data);
                    // setData(mock);
                })
                .catch((error) => {
                    console.error('Cannot load configuration');
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    //priprava dat po nacteni/zmene darkmode
    useEffect(
        () => {
            if (!data) return;
            setConfig({
                ...data,
                styles: data.styles[dark ? 'dark' : 'light']
            });
        },
        [data, dark]
    );

    return (
        <ConfigurationContext.Provider value={config}>
            {!!config && sessionId?.sessionId && children}
        </ConfigurationContext.Provider>
    );
}

export default Configuration;
